import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./HeaderSection.module.scss";
import {
  CaretDownIcon,
  DotsThreeIcon,
  EditIcon,
  EmailIcon,
  EntryPointsIcon,
  PhoneIcon,
  PlusIcon,
  TwitterIcon,
  UserIcon,
  XIcon
} from "@/frontend/shared/assets/icons";
import { Tag } from "@/frontend/shared/ui/TagV2";
import { LinkedInLettersIcon } from "@/frontend/shared/assets/icons/LinkedInLettersIcon";
import { ReactNode, useState } from "react";
import clsx from "clsx";
import { refactorWebsiteLink } from "@/frontend/shared/lib/refactorWebsiteLink/refactorWebsiteLink";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { moveCompanies } from "@/frontend/features/Company/model/moveCompanies";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AddPortfolioCompanyTagsModal } from "@/frontend/components/AddPortfolioCompanyTagsModal/ui/AddPortfolioCompanyTagsModal";
import { WebIcon } from "@/frontend/shared/assets/icons/WebIcon";
import { UnlockIcon } from "@/frontend/shared/assets/icons/UnlockIcon";
import { AxiosError } from "axios";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { useSetNewAnnotationVisible } from "@/frontend/atoms/annotations/annotations";
import { TagTabV2 } from "@/frontend/shared/ui/TagTabV2";
import { SuccessIcon } from "@/frontend/shared/assets/icons/SuccessIcon";
import { useOrgPhoto } from "@/frontend/shared/lib/hooks/useOrgPhoto";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { buildPipelineDropshadowOptions } from "../../lib/buildPipelineDropshadowOptions/buildPipelineDropshadowOptions";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import { getRouteDashboard } from "@/frontend/shared/const/router";
import { AddExpertModal } from "@/frontend/components/AddExpertModal";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import { SquareNormalButton } from "@/frontend/shared/ui/Button/SquareNormalButton/SquareNormalButton";
import { CommentIcon } from "@/frontend/shared/assets/icons/CommentIcon";
import { AddWebsiteModal } from "@/frontend/features/Company/ui/AddWebsiteModal/AddWebsiteModal";
import { InviteFounderModal } from "@/frontend/features/Company/ui/InviteFounderModal/InviteFounderModal";
import {
  EAccessDataStatus,
  EActiveView,
  EInvestorAccess,
  TCompanyHeaderInfo
} from "@/frontend/features/Company/model/types/types";
import { archiveCompany } from "@/frontend/features/Company/model/archiveCompany";
import { deleteCompany } from "@/frontend/features/Company/model/deleteCompany";
import { cancelRequest } from "@/frontend/features/Company/model/cancelRequest";
import { postAccessControlsRequest } from "@/frontend/features/Company/model/postAccessControlsRequest";
import { ELOCAL_STORAGE_KEY } from "@/frontend/shared/const/const";
import { TPipeline, TStageShortInfo } from "@/frontend/pages/DashboardPage/model/types/types";
import { useStageNames } from "@/frontend/features/Company/model/getStageNames";
import { AddTagsModalV2 } from "@/frontend/components/AddTagsModalV2/ui/AddTagsModalV2";
import { patchPortfolioBulkAssignTags } from "@/frontend/features/Company/model/patchPortfolioBulkAssignTags";
import { removeTagFromPortfolioCompany } from "@/frontend/features/Company/model/removeTagFromPortfolioCompany";
import { MoveModal } from "@/frontend/features/Company/ui/MoveCompany/MoveModal/MoveModal";

export function HeaderSection({
  data,
  activeView,
  onChangeActiveView,
  companyTags,
  companyId,
  stageName,
  triggerRefetch,
  refetchDetailsWithPolledData,
  dataStatusFromAccess,
  triggerRefetchAccess,
  isLoadingRequestAccess,
  setIsLoadingRequestAccess,
  isDataRoomPending,
  isContainerScrolled,
  setIsCommentsSectionOpen,
  pipeline
}: {
  data?: TCompanyHeaderInfo;
  activeView: EActiveView;
  onChangeActiveView: (view: EActiveView) => void;
  companyTags?: { id: number; name: string }[];
  companyId: number;
  stageName: string;
  triggerRefetch: () => void;
  refetchDetailsWithPolledData: () => void;
  dataStatusFromAccess?: EAccessDataStatus;
  triggerRefetchAccess: () => void;
  isLoadingRequestAccess?: boolean;
  setIsLoadingRequestAccess: (value: boolean) => void;
  setIsCommentsSectionOpen: (value: boolean) => void;
  isDataRoomPending?: boolean;
  isContainerScrolled?: boolean;
  pipeline?: { id: number; name: string };
}) {
  if (!data || !pipeline) return null;

  const { name, website, logoUrl, headline, linkedInUrl, twitterUrl, phone, email, id, harmonicTags } = data;
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [isAddWebsiteModalOpen, setIsAddWebsiteModalOpen] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isContextDropdownOpen, setIsContextDropdownOpen] = useState(false);
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [isDataSentDropdownOpen, setIsDataSentDropdownOpen] = useState(false);
  const [currentSelect, setCurrentSelect] = useState("");
  const [isInviteFoundersModalOpen, setIsInviteFoundersModalOpen] = useState(false);
  const [isMoveCompanyModalOpen, setIsMoveCompanyModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isInviteExpertModalOpen, setIsInviteExpertModalOpen] = useState(false);

  const setNewAnnotationEnabled = useSetNewAnnotationVisible();
  const { data: profileData } = useMe();
  const organizationImageUrl = useOrgPhoto(profileData?.organizationId);

  const { data: stages } = useStageNames(pipeline?.id);

  const options =
    stages?.map((stage: TStageShortInfo) => {
      return { value: String(stage.id), label: stage.name };
    }) ?? [];

  function getStageNameFromId({ v }: { v: string }): string {
    const result = options.find((option) => option.value === v)?.label || "";
    return result;
  }

  async function sendToArchive(id: number) {
    await archiveCompany([id])
      .then(() => {
        snackbarConstructor(`Successfully archived company ${data?.name}`, "normal");
        triggerRefetch();
      })
      .catch((error) => {
        snackbarConstructor(`Failed to archive company ${data?.name}`, "error");
        throw new Error(error);
      });
  }

  const navigate = useNavigate();

  async function sendToRemove(id: number) {
    await deleteCompany(id)
      .then(() => {
        snackbarConstructor(`Successfully removed company ${data?.name}`, "normal");
        triggerRefetch();
        navigate(getRouteDashboard());
      })
      .catch((error) => {
        snackbarConstructor(`Failed to remove company ${data?.name}`, "error");
        throw new Error(error);
      });
  }

  async function onCancelClick() {
    setIsDataSentDropdownOpen(false);
    await cancelRequest(data?.companyId || 0)
      .then(() => {
        snackbarConstructor("Data request cancelled", "normal");
        triggerRefetchAccess();
      })
      .catch((error) => {
        snackbarConstructor("Failed to cancel request", "error");
        throw new Error(error);
      });
  }

  function onArchiveClick() {
    setIsDropdownOpen(false);
    setIsArchiveModalOpen(true);
  }

  function onRemoveClick() {
    setIsDropdownOpen(false);
    setIsRemoveModalOpen(true);
  }

  function onEditDomainClick() {
    setIsContextDropdownOpen(false);
    setIsAddWebsiteModalOpen(true);
  }

  function onInviteExpertClick() {
    setIsContextDropdownOpen(false);
    setIsInviteExpertModalOpen(true);
  }

  async function moveCompaniesOnClick(v: string) {
    await moveCompanies(Number(v), [companyId || 0])
      .then(() => {
        const stageName = getStageNameFromId({ v });
        snackbarConstructor(`Successfully moved company ${data?.name} to ${stageName}`, "normal");
        setCurrentSelect(stageName);
        triggerRefetch();
      })
      .catch((error) => {
        snackbarConstructor(`Failed to move company ${data?.name} to ${stageName}`, "error");
        throw new Error(error);
      });
  }

  function shownButtonStatus() {
    switch (true) {
      case dataStatusFromAccess === EAccessDataStatus.NO_FOUNDER:
        return (
          <SquareNormalButton
            size="large"
            className={styles.inviteButton}
            onClick={() => setIsInviteFoundersModalOpen(true)}
          >
            <PlusIcon className={styles.plusIcon} />
            {!isMobile && "Invite Founders"}
          </SquareNormalButton>
        );
      case dataStatusFromAccess === EAccessDataStatus.NO_ACCESS:
        return (
          <SquareNormalButton
            size="large"
            variant="primary"
            className={styles.statusButton}
            onClick={onConfirmRequestDataAccess}
            isSubmitting={isLoadingRequestAccess}
          >
            <UnlockIcon className={styles.unlockIcon} />
            {!isMobile && "Request Data Access"}
          </SquareNormalButton>
        );
      case dataStatusFromAccess === EAccessDataStatus.PENDING_REQUEST:
        return (
          <div className={styles.dataRequestSentContainer}>
            <NormalButton
              size="large"
              variant="secondary"
              className={styles.statusButtonSent}
              onMouseDown={(e) => {
                e.stopPropagation();
                setIsDataSentDropdownOpen((prev) => !prev);
              }}
            >
              <Typography variant="label-4" color="greyscale900">
                {isMobile ? "Sent" : "Access Request sent"}
              </Typography>
              <CaretDownIcon className={clsx(styles.caretDownIcon, isDataSentDropdownOpen && styles.rotateIcon)} />
            </NormalButton>

            <DropshadowMenu
              isOpen={isDataSentDropdownOpen}
              onChange={() => setIsDataSentDropdownOpen((prev) => !prev)}
              options={[
                <div>
                  <NormalButton
                    className={styles.cancelRequest}
                    variant="tertiary"
                    size="small"
                    onClick={onCancelClick}
                    color="greyscale700"
                  >
                    <XIcon />
                    Cancel request
                  </NormalButton>
                </div>
              ]}
              onClose={() => setIsDataSentDropdownOpen(false)}
              top="40px"
              minWidth="155px"
              right="0"
            />
          </div>
        );
      default:
        return (
          <Tag type="custom" icon={<SuccessIcon />} className={styles.accessGrantedTag}>
            {!isMobile ? "Data Access Granted" : "Access Granted"}
          </Tag>
        );
    }
  }

  async function onConfirmRequestDataAccess() {
    setIsLoadingRequestAccess(true);
    try {
      await postAccessControlsRequest({
        companyId: data?.companyId || 0,
        access: EInvestorAccess.FULL
      });
      triggerRefetchAccess();
      snackbarConstructor("Access request sent", "normal");
    } catch (error) {
      snackbarConstructor("Request data access error", "error");
      reportErrorToSentry(error as AxiosError, "PORTFOLIO COMPANY PROFILE PAGE: /access-controls/request");
      setIsLoadingRequestAccess(false);
    }
  }

  function onClickDataRoom() {
    if (activeView === EActiveView.DATA_ROOM) return;

    onChangeActiveView(EActiveView.DATA_ROOM);

    if (isDataRoomPending) {
      triggerRefetchAccess();
    }
  }
  function displayedWebsiteField() {
    const content = website ? (
      <div className={styles.websiteContainerBlock}>
        <NormalButton variant="secondary" size="small" className={styles.websiteButton} color="greyscale700">
          <a href={refactorWebsiteLink(website)} target="_blank" rel="noreferrer" className={styles.websiteContainer}>
            <WebIcon />
            <Typography variant="label-5" color="greyscale700">
              {website}
            </Typography>
          </a>
        </NormalButton>
      </div>
    ) : (
      <div
        className={styles.plusContainer}
        onClick={() => {
          setIsAddWebsiteModalOpen(true);
        }}
      >
        <NormalButton variant="secondary" size="small" className={styles.websiteButton} color="greyscale700">
          {/* <WebIcon /> */}
          <PlusIcon className={styles.plusIcon} />
          <Typography variant="label-5" color="greyscale700">
            Add Website
          </Typography>
        </NormalButton>
      </div>
    );

    return content;
  }

  function displayedContactsField() {
    if (!email && !phone) return null;

    const options: ReactNode[] = [];
    if (phone) {
      options.push(
        <a href={`tel:${phone}`} target="_blank" rel="noreferrer" className={styles.phoneContainer}>
          <PhoneIcon className={styles.phoneIcon} />
          <Typography variant="label-4" color="greyscale600">
            {phone}
          </Typography>
        </a>
      );
    }

    if (email) {
      options.push(
        <a href={`mailto:${email}`} target="_blank" rel="noreferrer" className={styles.emailContainer}>
          <EmailIcon className={styles.emailIcon} />
          <Typography variant="label-4" color="greyscale600">
            {email}
          </Typography>
        </a>
      );
    }

    const handleChange = (value: ReactNode) => {
      setIsContactDropdownOpen(false);
    };

    return (
      <div className={styles.contactDropdownContainer}>
        <NormalButton
          // color="greyscale700"
          onMouseDown={(e) => {
            e.stopPropagation();
            setIsContactDropdownOpen((prev) => !prev);
          }}
          variant="secondary"
          size="small"
        >
          Contact
          <CaretDownIcon className={clsx(styles.caretDownIcon, isContactDropdownOpen && styles.rotateIcon)} />
        </NormalButton>
        <DropshadowMenu
          isOpen={isContactDropdownOpen}
          onChange={handleChange}
          options={options}
          onClose={() => setIsContactDropdownOpen(false)}
          minWidth="10px"
          width="fit-content"
          top="28px"
          right="0"
          optionClassName={styles.contactOption}
          dividerIndexRate={Array.from({ length: options.length }, (_, index) => index + 1)}
        />
      </div>
    );
  }
  const isMobile = useScreenWidth() <= 768;
  const showComments = useScreenWidth() >= 1050;
  const inviteContainer = (
    <div className={styles.inviteContainer}>
      <Tag type="custom">{pipeline.name}</Tag>

      <div className={styles.pipelineActions}>
        <DropshadowMenu
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          options={buildPipelineDropshadowOptions(
            options,
            currentSelect || stageName,
            (v) => {
              moveCompaniesOnClick(v);
              setIsDropdownOpen(false);
            },
            () => {
              setIsMoveCompanyModalOpen(true);
              setIsDropdownOpen(false);
            },
            onArchiveClick,
            onRemoveClick
          )}
          dividerIndexRate={3}
          className={styles.selectionDropdown}
          top="40px"
          right="0"
        />

        <SquareNormalButton
          customBreakpoint={1049}
          size="large"
          variant="secondary"
          onMouseDown={(e) => {
            e.stopPropagation();
            setIsDropdownOpen((prev) => !prev);
          }}
        >
          <EntryPointsIcon />
          {showComments && (
            <>
              <span className={styles.pipelineActionsButtonText}>{currentSelect || stageName}</span>
              <CaretDownIcon className={clsx(styles.caretDownIcon, isDropdownOpen && styles.rotateIcon)} />
            </>
          )}
        </SquareNormalButton>
      </div>
      {shownButtonStatus()}

      <NormalButton size="large" variant="tertiary" onClick={() => setIsContextDropdownOpen(true)}>
        <DotsThreeIcon />
      </NormalButton>
      <DropshadowMenu
        isOpen={isContextDropdownOpen}
        onClose={() => setIsContextDropdownOpen(false)}
        onChange={() => setIsContextDropdownOpen(false)}
        options={[
          <div className={styles.dropshadowOption}>
            <NormalButton
              variant="tertiary"
              size="medium"
              color="greyscale900"
              onClick={onEditDomainClick}
              className={styles.dropshadowOptionButton}
            >
              <EditIcon />
              Edit Domain
            </NormalButton>
          </div>,
          <div className={styles.dropshadowOption}>
            <NormalButton
              variant="tertiary"
              size="medium"
              color="greyscale900"
              onClick={onInviteExpertClick}
              className={styles.dropshadowOptionButton}
            >
              <UserIcon />
              Invite an Expert
            </NormalButton>
          </div>
        ]}
        right="0"
        dividerIndexRate={1}
        top="60px"
      />
    </div>
  );

  const addTagsFn = async (id: number, tags: string[]) => {
    await patchPortfolioBulkAssignTags([id], tags);
  };

  const removeTagsFn = async (id: number, tagIds: number[]) => {
    await removeTagFromPortfolioCompany({ tagIds, companyId: id });
  };

  return (
    <div className={clsx(styles.container, activeView === EActiveView.DATA_ROOM && styles.notSticky)}>
      <div className={styles.block}>
        <div className={styles.upperContent}>
          <div className={styles.logoContainer}>
            {logoUrl ? (
              <img src={logoUrl} alt={name} className={clsx(styles.logo)} />
            ) : (
              <div className={clsx(styles.logoPlaceholder)} />
            )}
            {isMobile && inviteContainer}
          </div>
          <div className={styles.infoContainer}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>
                <Typography variant="label-1" color="greyscale900">
                  {name}
                </Typography>
              </div>
              <div className={styles.info}>
                {displayedWebsiteField()}
                {displayedContactsField()}
                <div className={styles.iconsContainer}>
                  {twitterUrl && (
                    <a href={twitterUrl} target="_blank" rel="noreferrer" className={styles.twitterIconContainer}>
                      <TwitterIcon className={styles.twitterIcon} />
                    </a>
                  )}
                  {linkedInUrl && (
                    <a href={linkedInUrl} target="_blank" rel="noreferrer" className={styles.linkedinIconContainer}>
                      <LinkedInLettersIcon className={styles.linkedinIcon} />
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div
              className={clsx(styles.descriptionBlock, !isContainerScrolled && isMobile && styles.hiddenDescription)}
            >
              <div className={styles.subDescription}>
                <Typography variant="body-2" color="greyscale800" className={styles.shortDescription}>
                  {headline}
                </Typography>
              </div>
            </div>
          </div>
          {!isMobile && inviteContainer}
        </div>
        <div className={clsx(styles.tagsContainerBlock, !isContainerScrolled && styles.hiddenTagsContainer)}>
          <div className={clsx(styles.tagsContainer)}>
            <div className={styles.customTagsContainer}>
              <TagTabV2
                customTags={true}
                addTagClick={() => setIsAddTagsModalOpen(true)}
                logoSrc={organizationImageUrl}
              >
                {companyTags?.map((tag: { name: string; id: number }, index: number) => {
                  return <Tag children={tag.name} type="custom" key={index} className={styles.whiteTag} />;
                })}
              </TagTabV2>
            </div>
            {!!harmonicTags && harmonicTags.length > 0 && (
              <div className={styles.innoveraTagsContainer}>
                <TagTabV2 customTags={false}>
                  {harmonicTags?.map((tag: string, index: number) => {
                    return <Tag children={tag} type="innovera" key={index} className={styles.tag} />;
                  })}
                </TagTabV2>
              </div>
            )}
          </div>
        </div>
        <div className={styles.bottomContent}>
          <div className={styles.viewSwitcherContainer}>
            <Typography
              variant="label-4"
              color="greyscale500"
              onClick={() => onChangeActiveView(EActiveView.PROFILE)}
              className={clsx(styles.option, activeView === EActiveView.PROFILE && styles.active)}
            >
              Profile
            </Typography>
            <Typography
              variant="label-4"
              color="greyscale500"
              onClick={onClickDataRoom}
              className={clsx(styles.option, activeView === EActiveView.DATA_ROOM && styles.active)}
            >
              Data Room
            </Typography>
            <Typography
              variant="label-4"
              color="greyscale500"
              onClick={() => onChangeActiveView(EActiveView.ANNOTATIONS)}
              className={clsx(styles.option, activeView === EActiveView.ANNOTATIONS && styles.active)}
            >
              Notes
            </Typography>
          </div>
          {!showComments && activeView === EActiveView.PROFILE && (
            <NormalButton variant="tertiary" size="large" onClick={() => setIsCommentsSectionOpen(true)}>
              <CommentIcon />
            </NormalButton>
          )}
        </div>
      </div>
      <AddWebsiteModal
        isOpen={isAddWebsiteModalOpen}
        onClose={() => setIsAddWebsiteModalOpen(false)}
        id={companyId}
        triggerRefetch={refetchDetailsWithPolledData}
        existedWebsite={website || ""}
      />
      <AddExpertModal
        companyId={companyId}
        isOpen={isInviteExpertModalOpen}
        onClose={() => setIsInviteExpertModalOpen(false)}
        triggerRefetch={triggerRefetch}
      />
      <AddTagsModalV2
        id={companyId || 0}
        triggerRefetch={triggerRefetch}
        isOpen={isAddTagsModalOpen}
        onClose={() => setIsAddTagsModalOpen(false)}
        currentTags={companyTags?.map((tag) => tag.name) || []}
        addTagsFn={addTagsFn}
        removeTagsFn={removeTagsFn}
      />
      <InviteFounderModal
        isOpen={isInviteFoundersModalOpen}
        onClose={() => setIsInviteFoundersModalOpen(false)}
        id={companyId}
      />
      <MoveModal
        id={companyId}
        isOpen={isMoveCompanyModalOpen}
        onClose={() => setIsMoveCompanyModalOpen(false)}
        triggerRefetch={triggerRefetch}
        initialPipeline={pipeline.name}
      />
      <ConfirmationModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onConfirm={() => {
          sendToArchive(companyId);
          setIsArchiveModalOpen(false);
        }}
        confirmButtonText="Archive"
        title="Move company to archive?"
        content="Once archived, the company will be removed from the pipeline but can be restored at any time if needed. No data will be deleted, but the company will no longer appear in the pipeline view."
      />
      <ConfirmationModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={() => {
          sendToRemove(companyId);
          setIsRemoveModalOpen(false);
        }}
        confirmButtonText="Remove"
        title="Remove company from pipeline?"
        content="Removing this company will take it out of the pipeline permanently. No data will be deleted, but the company will no longer appear in the pipeline."
        dangerConfirm
      />
    </div>
  );
}
